import { useDebouncedCallback } from "@react-hookz/web";
import type { FunctionComponent, ReactNode } from "react";
import { createContext, useCallback, useContext, useRef } from "react";

import { useCurrentPageType } from "shared/hooks/current-page-type";

import {
  Tier1AdSlotDeadline,
  Tier1AdSlots,
  Tier1AdSlotWaitingTime,
  Tier2AdSlotDeadline,
  Tier2AdSlots,
  Tier2AdSlotWaitingTime,
  Tier3AdSlotDeadline,
  Tier3AdSlotWaitingTime,
} from "./consts";
import type { AdUnit } from "./types";

type ContextValue = {
  refresh?: RefreshAdSlotFunction;
};

type RefreshAdSlotFunction = (slots?: googletag.Slot[] | null) => void;

const AdSlotContext = createContext<ContextValue>({});

type Props = {
  children?: ReactNode;
};

export const AdSlotContextProvider: FunctionComponent<Props> = ({ children }) => {
  const currentPageType = useCurrentPageType();

  const tier1SlotsReference = useRef<googletag.Slot[]>([]);
  const tier2SlotsReference = useRef<googletag.Slot[]>([]);
  const tier3SlotsReference = useRef<googletag.Slot[]>([]);

  const executeTier1 = useDebouncedCallback(
    () =>
      (function () {
        const slots = [...tier1SlotsReference.current];
        tier1SlotsReference.current = [];

        if (slots.length > 0) {
          googletag.cmd.push(function () {
            googletag.pubads().refresh(slots);
          });
        }
      })(),
    [],
    Tier1AdSlotWaitingTime,
    Tier1AdSlotDeadline,
  );

  const executeTier2 = useDebouncedCallback(
    () =>
      (function () {
        const slots = [...tier2SlotsReference.current];
        tier2SlotsReference.current = [];

        if (slots.length > 0) {
          googletag.cmd.push(function () {
            googletag.pubads().refresh(slots);
          });
        }
      })(),
    [],
    Tier2AdSlotWaitingTime,
    Tier2AdSlotDeadline,
  );

  const executeTier3 = useDebouncedCallback(
    () =>
      (function () {
        const slots = [...tier3SlotsReference.current];
        tier3SlotsReference.current = [];

        if (slots.length > 0) {
          googletag.cmd.push(function () {
            googletag.pubads().refresh(slots);
          });
        }
      })(),
    [],
    Tier3AdSlotWaitingTime,
    Tier3AdSlotDeadline,
  );

  const refresh = useCallback<RefreshAdSlotFunction>(
    slots => {
      for (const slot of slots ?? []) {
        const adUnit = slot.getAdUnitPath().split("/").pop() as AdUnit;

        if (Tier1AdSlots[currentPageType]?.has(adUnit)) {
          tier1SlotsReference.current.push(slot);
          continue;
        }
        if (Tier2AdSlots[currentPageType]?.has(adUnit)) {
          tier2SlotsReference.current.push(slot);
          continue;
        }
        tier3SlotsReference.current.push(slot);
      }
      executeTier1();
      executeTier2();
      executeTier3();
    },
    [currentPageType, executeTier1, executeTier2, executeTier3],
  );

  return <AdSlotContext.Provider value={{ refresh }}>{children}</AdSlotContext.Provider>;
};

export const useAdSlotContext = () => useContext(AdSlotContext);
